import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search,
  Users,
  Building2,
  Eye,
  KeyRound,
  Shield,
  LogOut,
  UserX,
  MoreVertical,
  Mail,
  AlertTriangle,
  Clock,
  Loader2,
  Zap,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OwnerAdminUsers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  // Impersonation dialog state
  const [impersonateDialog, setImpersonateDialog] = useState({ open: false, user: null });
  const [impersonateReason, setImpersonateReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);

  // Delete-account (test/QA cleanup) dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/users/search?q=${encodeURIComponent(searchQuery)}`, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        toast.error('Search failed');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleImpersonate = async () => {
    if (!impersonateReason.trim()) {
      toast.error('Please provide a reason for impersonation');
      return;
    }
    
    setImpersonating(true);
    // Open the tab synchronously (within the click gesture) so pop-up
    // blockers don't kill it; point it at the session URL after the API call.
    const win = window.open('about:blank', '_blank');
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/users/${impersonateDialog.user.user_id}/impersonate`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: impersonateDialog.user.user_id, reason: impersonateReason })
      });
      
      if (response.ok) {
        const data = await response.json();
        const url = `/impersonate?token=${encodeURIComponent(data.token)}`;
        if (win && !win.closed) {
          win.location.replace(url);
          toast.success('Impersonation session started');
        } else {
          toast.success('Impersonation session ready', {
            duration: 15000,
            action: { label: 'Open client session', onClick: () => window.open(url, '_blank') },
          });
        }
        setImpersonateDialog({ open: false, user: null });
        setImpersonateReason('');
      } else {
        if (win) win.close();
        const error = await response.json();
        toast.error(error.detail || 'Failed to start impersonation');
      }
    } catch (error) {
      if (win) win.close();
      toast.error('Failed to start impersonation');
    } finally {
      setImpersonating(false);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/users/${userId}/reset-password`, {
        credentials: 'include',
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Password reset email sent');
      } else {
        toast.error('Failed to send password reset');
      }
    } catch (error) {
      toast.error('Failed to send password reset');
    }
  };

  const handleSendMagicLink = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/users/${userId}/magic-link`, {
        credentials: 'include',
        method: 'POST'
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(data.message || 'Magic sign-in link sent');
      } else {
        toast.error(data.detail || 'Failed to send magic link');
      }
    } catch (error) {
      toast.error('Failed to send magic link');
    }
  };

  const handleDeleteAccount = async () => {
    const user = deleteDialog.user;
    if (!user) return;
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/users/${user.user_id}?delete_stripe=true`, {
        credentials: 'include',
        method: 'DELETE',
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(`Deleted ${user.email}`);
        setUsers((prev) => prev.filter((u) => u.user_id !== user.user_id));
        setDeleteDialog({ open: false, user: null });
        setDeleteConfirm('');
      } else {
        toast.error(data.detail || 'Failed to delete account');
      }
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">User Search</h1>
        <p className="text-muted-foreground">Search and manage users across all workspaces</p>
      </div>

      {/* Search Box */}
      <Card className="bg-card border-white/15">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email, name, phone, or user ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10 bg-white/10 border-white/20 text-white"
                data-testid="user-search-input"
              />
            </div>
            <Button 
              onClick={handleSearch}
              disabled={loading || !searchQuery.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="user-search-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-2">Search</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {!searched ? (
        <Card className="bg-card border-white/15">
          <CardContent className="py-12 text-center">
            <Search className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-white font-medium">Look up any user across every workspace</p>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              This page searches the <strong className="text-muted-foreground/90">users</strong> table SaaS-wide — so you can find someone even if you don&apos;t know which customer workspace they belong to.
              Search by <strong className="text-muted-foreground/90">email</strong>, <strong className="text-muted-foreground/90">name</strong>, <strong className="text-muted-foreground/90">phone</strong>, or <strong className="text-muted-foreground/90">user_id</strong>. Once found, you can Impersonate, Reset Password, or Send Magic Link right from the results.
            </p>
            <p className="text-xs text-muted-foreground/70 mt-3">
              Prefer per-workspace context? Use <strong>Customers</strong> in the left nav instead.
            </p>
          </CardContent>
        </Card>
      ) : loading ? (
        <Card className="bg-card border-white/15">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 mx-auto text-emerald-500 animate-spin mb-3" />
            <p className="text-muted-foreground">Searching...</p>
          </CardContent>
        </Card>
      ) : users.length === 0 ? (
        <Card className="bg-card border-white/15">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No users found</p>
            <p className="text-sm text-muted-foreground mt-1">Try a different search term</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{users.length} user(s) found</p>
          {users.map((user) => (
            <Card key={user.user_id} className="bg-card border-white/15 hover:border-white/20 transition-colors">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <span className="text-lg font-medium text-emerald-400">
                      {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                    </span>
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-white truncate">{user.name || 'Unnamed User'}</h3>
                      {user.auth_provider && (
                        <Badge variant="outline" className="border-white/20 text-muted-foreground text-xs">
                          {user.auth_provider}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                    {user.phone && (
                      <p className="text-xs text-muted-foreground">{user.phone}</p>
                    )}
                  </div>

                  {/* Workspaces */}
                  <div className="hidden md:block">
                    {user.workspaces?.length > 0 ? (
                      <div className="space-y-1">
                        {user.workspaces.slice(0, 2).map((ws) => (
                          <Link 
                            key={ws.workspace_id}
                            to={`/owner-admin/customers/${ws.workspace_id}`}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-emerald-400"
                          >
                            <Building2 className="h-3 w-3" />
                            {ws.name}
                          </Link>
                        ))}
                        {user.workspaces.length > 2 && (
                          <span className="text-xs text-muted-foreground">+{user.workspaces.length - 2} more</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">No workspaces</span>
                    )}
                  </div>

                  {/* Last Login */}
                  <div className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatDate(user.last_login)}
                  </div>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-white">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setImpersonateDialog({ open: true, user })}>
                        <Eye className="h-4 w-4 mr-2" />
                        Impersonate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleResetPassword(user.user_id)} data-testid={`users-reset-password-${user.user_id}`}>
                        <KeyRound className="h-4 w-4 mr-2" />
                        Reset Password
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSendMagicLink(user.user_id)} data-testid={`users-magic-link-${user.user_id}`}>
                        <Zap className="h-4 w-4 mr-2" />
                        Send Magic Link
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Shield className="h-4 w-4 mr-2" />
                        Reset 2FA
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Email
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>
                        <LogOut className="h-4 w-4 mr-2" />
                        Force Logout
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-400 focus:text-red-400"
                        onClick={() => { setDeleteConfirm(''); setDeleteDialog({ open: true, user }); }}
                        data-testid={`users-delete-account-${user.user_id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Account
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Impersonation Dialog */}
      <Dialog open={impersonateDialog.open} onOpenChange={(open) => !open && setImpersonateDialog({ open: false, user: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Impersonate User</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              You are about to view the app as <strong className="text-white">{impersonateDialog.user?.name || impersonateDialog.user?.email}</strong>.
              This action will be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
                <div className="text-sm text-yellow-200">
                  <p className="font-medium">Important</p>
                  <p>All actions during impersonation are logged and attributed to your admin account.</p>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Reason for impersonation *</label>
              <Textarea
                placeholder="e.g., Troubleshooting user-reported issue #1234"
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImpersonateDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleImpersonate}
              disabled={impersonating || !impersonateReason.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {impersonating ? 'Starting...' : 'Start Impersonation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog (test/QA cleanup) */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, user: null })}>
        <DialogContent className="bg-card border-white/15" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Delete account permanently?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This permanently deletes <strong className="text-white">{deleteDialog.user?.email}</strong>,
              their owned workspaces, sessions and billing record, and (best-effort) their Stripe
              customer &amp; subscriptions. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                <div className="text-sm text-red-200">
                  <p className="font-medium">Irreversible</p>
                  <p>Use this only for test / QA accounts. Type <strong>DELETE</strong> to confirm.</p>
                </div>
              </div>
            </div>
            <Input
              placeholder="Type DELETE to confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="bg-white/10 border-white/20 text-white"
              data-testid="delete-account-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm.trim().toUpperCase() !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-account-confirm-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
